import { useSyncExternalStore, type ReactNode } from 'react'
import { ifcxApi } from '@viewer/ifcx/ifcxApi'
import { propriedadesDoNo } from '@viewer/ifcx/ifcxParse'

/** O elemento IFC5 seleccionado, no painel de propriedades.
 *
 *  Um `.ifcx` tem selecção própria (não há `{modelId, localId}` num
 *  ficheiro que não passa pelo motor), e até aqui o painel de
 *  propriedades não sabia dela: clicava-se numa parede do IFC5 e o
 *  painel continuava a dizer "clica num elemento". Isto é a ponte:
 *  quando não há elemento IFC seleccionado e há um nó IFC5, mostra-se
 *  ele, com as mesmas propriedades do painel do IFC5. */
export default function IfcxElementoView({ semNada }: { semNada: ReactNode }) {
  const seleccao = useSyncExternalStore(ifcxApi.subscribe, ifcxApi.seleccionado, ifcxApi.seleccionado)
  const lista = useSyncExternalStore(ifcxApi.subscribe, ifcxApi.lista, ifcxApi.lista)
  const entrada = seleccao ? lista.find((e) => e.id === seleccao.entradaId) : undefined
  const elemento = entrada?.cena.elementos.find((e) => e.path === seleccao?.path)
  if (!entrada || !elemento) return <>{semNada}</>
  const { propriedades, colisoes } = propriedadesDoNo(elemento.atributos)

  return (
    <div className="flex h-full flex-col text-sm">
      <div className="border-b border-[var(--color-border)] px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
          IFC5 · {elemento.classe ?? 'sem classe'}
        </div>
        <div className="font-medium text-[var(--color-text)]">{elemento.nome || elemento.path}</div>
        <div className="truncate text-[10px] text-[var(--color-text-dim)]" title={elemento.path}>
          {entrada.ficheiro} · {elemento.caminho || elemento.path}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {propriedades.length === 0 ? (
          <div className="text-[11px] italic text-[var(--color-text-muted)]">Este nó não tem propriedades.</div>
        ) : (
          <table className="w-full table-fixed border-collapse text-[11px]">
            <tbody>
              {propriedades.map((p) => (
                <tr key={p.chave} className="border-b border-[var(--color-border)]/40 last:border-b-0">
                  <td className="w-1/2 break-words py-1 pr-2 align-top text-[var(--color-text-muted)]" title={p.chave}>
                    {p.pset && <span className="text-[var(--color-text-dim)]">{p.pset} · </span>}
                    {p.nome}
                  </td>
                  <td className="break-words py-1 align-top text-[var(--color-text)]">{comoTexto(p.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {colisoes.length > 0 && (
          <p className="mt-2 text-[10px] text-[var(--color-warn-text)]">
            Nome repetido em property sets diferentes:{' '}
            {colisoes.map((c) => `${c.nome} (${c.origens.map((o) => o.pset ?? 'sem pset').join(', ')})`).join('; ')}.
            Mostram-se todos.
          </p>
        )}
        <p className="mt-3 text-[10px] text-[var(--color-text-dim)]">
          Os nós IFC5 não têm GlobalId nem entram nos módulos que falam de elementos IFC (BCF, IDS, régua). Isto é o
          que o ficheiro diz deles.
        </p>
      </div>
    </div>
  )
}

function comoTexto(v: unknown): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (v && typeof v === 'object') {
    const c = (v as { code?: unknown }).code
    if (typeof c === 'string') return c
  }
  return JSON.stringify(v) ?? ''
}
